import { Image as ImageLayer, Tile as TileLayer } from 'ol/layer';
import ImageWMS from 'ol/source/ImageWMS';
import TileWmsSource from 'ol/source/TileWMS';
import OsmSource from 'ol/source/OSM';
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile'
import MvtFormat from 'ol/format/MVT'
import GeoJsonFormat from 'ol/format/GeoJSON'
import TopoJsonFormat from 'ol/format/TopoJSON'
import KmlFormat from 'ol/format/KML'
import GML2Format from 'ol/format/GML2'
import GML3Format from 'ol/format/GML3'
import GML32Format from 'ol/format/GML32'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import XyzSource from 'ol/source/XYZ'
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { OlStyleFactory } from './OlStyle'
import { applyTransform } from 'ol/extent';
import { getTransform } from 'ol/proj';
import axios from '../axios';
import ObjectUtil from '../util/Object';

/**
 * Factory, which creates OpenLayers layer instances according to a given config
 * object.
 */
export const LayerFactory = {

  /**
   * Maps the format literal of the config to the corresponding OL module.
   * @type {Object}
   */
  formatMapping: {
    MVT: MvtFormat,
    GeoJSON: GeoJsonFormat,
    TopoJSON: TopoJsonFormat,
    KML: KmlFormat,
    GML2: GML2Format,
    GML3: GML3Format,
    GML32: GML32Format
  },

  /**
   * Maps the format literal of the config to the corresponding WFS outputFormat.
   * @type {Object}
   */
  wfsFormatMapping: {
    GeoJSON: 'application/json',
    GML2: 'text/xml; subtype=gml/2.1.2',
    GML3: 'text/xml; subtype=gml/3.1.1',
    GML32: 'text/xml; subtype=gml/3.2'
  },

  /**
   * Returns an OpenLayers layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @param  {ol/Map} olMap  Optional OL map we work on
   * @return {ol/layer/Base} OL layer instance
   */
  getInstance (lConf, olMap) {
    // create correct layer type
    if (lConf.type === 'TILEWMS') {
      return this.createTileWmsLayer(lConf);
    } else if (lConf.type === 'IMAGEWMS') {
      return this.createImageWmsLayer(lConf);
    } else if (lConf.type === 'WFS') {
      return this.createWfsLayer(lConf, olMap);
    } else if (lConf.type === 'XYZ') {
      return this.createXyzLayer(lConf);
    } else if (lConf.type === 'OSM') {
      return this.createOsmLayer(lConf);
    } else if (lConf.type === 'VECTOR') {
      return this.createVectorLayer(lConf);
    } else if (lConf.type === 'VECTORTILE') {
      return this.createVectorTileLayer(lConf);
    } else {
      return null;
    }
  },

  /**
   * Returns an OL layer options initialization object, containing
   * attributes common to all layer types.
   * @param  {Object} lConf  Layer config object
   * @return {Object} OL layer options
   */
  getCommonLayerOptions (lConf) {
    return {
      lid: lConf.lid,
      isBaseLayer: lConf.isBaseLayer,
      previewImage: lConf.previewImage,
      displayInLayerList: lConf.displayInLayerList,
      supportsPermalink: lConf.supportsPermalink,
      extent: lConf.extent,
      visible: lConf.visible,
      opacity: lConf.opacity,
      opacityControl: lConf.opacityControl,
      zIndex: lConf.zIndex,
      confName: lConf.name,
      confAttributions: lConf.attributions,
      legend: lConf.legend,
      legendUrl: lConf.legendUrl,
      legendOptions: lConf.legendOptions
    };
  },

  /**
   * Returns an OpenLayers WMS layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Image} OL WMS layer instance
   */
  createImageWmsLayer (lConf) {
    // apply additional HTTP params
    const params = { LAYERS: lConf.layers };
    ObjectUtil.mergeDeep(params, lConf.params);

    const layer = new ImageLayer({
      ...this.getCommonLayerOptions(lConf),
      source: new ImageWMS({
        url: lConf.url,
        params,
        serverType: lConf.serverType,
        ratio: lConf.ratio,
        interpolate: lConf.interpolate,
        projection: lConf.projection,
        crossOrigin: lConf.crossOrigin,
        hoverable: lConf.hoverable,
        hoverAttribute: lConf.hoverAttribute,
        hoverOverlay: lConf.hoverOverlay
      })
    });

    return layer;
  },

  /**
   * Returns an OpenLayers Tiled WMS layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL Tiled WMS layer instance
   */
  createTileWmsLayer (lConf) {
    // apply additional HTTP params
    const params = { LAYERS: lConf.layers };
    ObjectUtil.mergeDeep(params, lConf.params);

    const layer = new TileLayer({
      ...this.getCommonLayerOptions(lConf),
      source: new TileWmsSource({
        url: lConf.url,
        params,
        serverType: lConf.serverType,
        tileGrid: lConf.tileGrid,
        projection: lConf.projection,
        crossOrigin: lConf.crossOrigin,
        hoverable: lConf.hoverable,
        hoverAttribute: lConf.hoverAttribute,
        hoverOverlay: lConf.hoverOverlay,
        // 自定义tileLoadFunction
        tileLoadFunction: function (image, src) {
          // 创建一个HTTP请求来获取图像
          const xhr = new XMLHttpRequest();
          xhr.open('GET', src);
          xhr.responseType = 'blob'; // 期望的响应类型是blob
          // 设置Authorization请求头
          xhr.setRequestHeader('Authorization', 'Basic ' + window.btoa('data_services:data_services'));

          // 请求加载成功时的处理
          xhr.onload = function () {
            if (this.status === 200) {
              const blob = this.response;
              // 创建一个对象URL
              const objectUrl = URL.createObjectURL(blob);
              image.getImage().src = objectUrl;
            }
          };

          // 发送请求
          xhr.send();
        }
      })
    });

    return layer;
  },

  /**
   * Returns an OpenLayers WFS layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @param  {ol/Map} olMap  The OpenLayers map we work on
   * @return {ol.layer.Tile} OL WFS layer instance
   */
  createWfsLayer: function (lConf, olMap) {
    const mapSrs = olMap.getView().getProjection().getCode();
    // set a default projection if not set in config
    if (!lConf.projection) {
      lConf.projection = mapSrs;
    }
    // set a default WFS version if not set in config
    if (!lConf.version) {
      lConf.version = '1.1.0';
    }
    if (!lConf.format) {
      lConf.format = 'GML3';
    }

    // detect the WFS output format
    const outputFormat = this.wfsFormatMapping[lConf.format];

    // overwrite format options, so they fit to the SRS (map vs. data)
    if (mapSrs !== lConf.projection) {
      lConf.formatConfig.dataProjection = lConf.projection;
      lConf.formatConfig.featureProjection = mapSrs;
    }

    const vectorSource = new VectorSource({
      format: new this.formatMapping[lConf.format](lConf.formatConfig),
      loader: (extent) => {
        // assemble WFS GetFeature request
        const pre = lConf.url.includes('?') ? '&' : '?';
        let wfsRequest = lConf.url + pre + 'service=WFS&' +
          'version=' + lConf.version + '&request=GetFeature&' +
          'typename=' + lConf.typeName + '&' +
          'outputFormat=' + outputFormat + '&srsname=' + lConf.projection;

        // add WFS version dependent feature limitation
        if (Number.isInteger(parseInt(lConf.maxFeatures))) {
          if (lConf.version.startsWith('1.')) {
            wfsRequest += '&maxFeatures=' + lConf.maxFeatures;
          } else {
            wfsRequest += '&count=' + lConf.maxFeatures;
          }
        }
        // add bbox filter
        if (lConf.loadOnlyVisible !== false) {
          if (mapSrs !== lConf.projection) {
            extent = applyTransform(extent, getTransform(mapSrs, lConf.projection));
          }
          wfsRequest += '&bbox=' + extent.join(',') + ',' + lConf.projection + '';
        }

        // load data from WFS, parse and add to vector source
        const request = {
          method: 'GET',
          url: wfsRequest
        };
        axios(request)
          .then(response => {
            const feats = vectorSource.getFormat().readFeatures(response.data);
            vectorSource.addFeatures(feats);
          })
          .catch(() => {
            vectorSource.removeLoadedExtent(extent);
          });
      },
      strategy: lConf.loadOnlyVisible !== false ? bboxStrategy : undefined
    });

    const vector = new VectorLayer({
      ...this.getCommonLayerOptions(lConf),
      source: vectorSource,
      style: OlStyleFactory.getInstance(lConf.style),
      columnMapping: lConf.columnMapping,
      hoverable: lConf.hoverable,
      hoverAttribute: lConf.hoverAttribute,
      hoverOverlay: lConf.hoverOverlay
    });

    return vector;
  },

  /**
   * Returns an XYZ based tile layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL XYZ layer instance
   */
  createXyzLayer (lConf) {
    const xyzLayer = new TileLayer({
      ...this.getCommonLayerOptions(lConf),
      source: new XyzSource({
        url: lConf.url,
        tileGrid: lConf.tileGrid,
        projection: lConf.projection,
        crossOrigin: lConf.crossOrigin
      })
    });

    return xyzLayer;
  },

  /**
   * Returns an OpenLayers OSM layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL OSM layer instance
   */
  createOsmLayer (lConf) {
    const layer = new TileLayer({
      ...this.getCommonLayerOptions(lConf),
      source: new OsmSource({
        crossOrigin: lConf.crossOrigin
      })
    });

    return layer;
  },

  /**
   * Returns an OpenLayers vector layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Vector} OL vector layer instance
   */
  createVectorLayer (lConf) {
    const format = new this.formatMapping[lConf.format](lConf.formatConfig);
    const sourceOptions = {
      format
    };

    const vectorSource = new VectorSource(sourceOptions);

    if (lConf.url) {
      let currentUrl = lConf.url;

      vectorSource.setLoader(function (extent, resolution, projection) {
        const proj = projection.getCode();
        axios.get(currentUrl).then(response => {
          const features = format.readFeatures(response.data, {
            featureProjection: proj
          });
          this.addFeatures(features);
        }).catch(error => {
          console.error('Error loading vector layer', error);
          this.removeLoadedExtent(extent);
        });
      });

      // Preserve dynamic URL support for callers that update village filters at runtime.
      vectorSource.getUrl = function () {
        return currentUrl;
      };

      vectorSource.setUrl = function (newUrl) {
        currentUrl = newUrl;
      };
    }

    const vectorLayer = new VectorLayer({
      ...this.getCommonLayerOptions(lConf),
      source: vectorSource,
      style: OlStyleFactory.getInstance(lConf.style),
      columnMapping: lConf.columnMapping,
      hoverable: lConf.hoverable,
      hoverAttribute: lConf.hoverAttribute,
      hoverOverlay: lConf.hoverOverlay
    });

    return vectorLayer;
  },

  /**
   * Returns an OpenLayers vector tile layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.VectorTile} OL vector tile layer instance
   */
  createVectorTileLayer (lConf) {
    const vtLayer = new VectorTileLayer({
      ...this.getCommonLayerOptions(lConf),
      source: new VectorTileSource({
        url: lConf.url,
        format: new this.formatMapping[lConf.format](),
        tileGrid: lConf.tileGrid,
        projection: lConf.projection
      }),
      style: OlStyleFactory.getInstance(lConf.style),
      hoverable: lConf.hoverable,
      hoverAttribute: lConf.hoverAttribute,
      hoverOverlay: lConf.hoverOverlay
    });

    return vtLayer;
  },

  /**
   * Update or apply a CQL_FILTER on a WMS layer's source.
   * Works for both TileWMS and ImageWMS. Prefers updateParams when available;
   * otherwise replaces the source while preserving existing params and options
   * such as serverType, crossOrigin, and tileLoadFunction (for TileWMS).
   *
   * @param {import('ol/layer/Base').default} layer - The OL layer instance
   * @param {string} cql - The CQL filter string. Empty string clears the filter.
   */
  setCqlFilterOnLayer (layer, cql) {
    if (!layer || typeof layer.getSource !== 'function') return;
    const src = layer.getSource();
    if (!src) return;

    // Prefer native updateParams when available
    if (typeof src.updateParams === 'function') {
      try {
        src.updateParams({ CQL_FILTER: cql });
        return;
      } catch (e) {
        // fall through to replacement strategy
      }
    }

    // Replacement strategy: reconstruct source with merged params
    const url = (typeof src.getUrls === 'function' && src.getUrls() && src.getUrls()[0]) ||
      (typeof src.getUrl === 'function' && src.getUrl()) || undefined;
    const existingParams = (typeof src.getParams === 'function' && src.getParams()) || {};
    const params = { ...existingParams, CQL_FILTER: cql };

    // Try to preserve these options where possible
    const crossOrigin = typeof src.getCrossOrigin === 'function' ? src.getCrossOrigin() : undefined;
    const serverType = existingParams.serverType || 'geoserver';

    let newSource;
    try {
      if (src instanceof TileWmsSource) {
        const tileGrid = typeof src.getTileGrid === 'function' ? src.getTileGrid() : undefined;
        const tileLoadFunction = typeof src.getTileLoadFunction === 'function' ? src.getTileLoadFunction() : undefined;
        newSource = new TileWmsSource({ url, params, serverType, crossOrigin, tileGrid, tileLoadFunction });
      } else if (src instanceof ImageWMS) {
        newSource = new ImageWMS({ url, params, serverType, crossOrigin });
      } else {
        // Default to TileWMS if type is unknown
        newSource = new TileWmsSource({ url, params, serverType, crossOrigin });
      }
    } catch (err) {
      // As last resort, try updateParams again
      if (typeof src.updateParams === 'function') {
        src.updateParams({ CQL_FILTER: cql });
      }
      return;
    }

    layer.setSource(newSource);
  },

  /**
   * Convenience to set CQL filter by layer id (lid) on a given map.
   * @param {import('ol/Map').default} olMap
   * @param {string} lid
   * @param {string} cql
   */
  setCqlFilterByLid (olMap, lid, cql) {
    if (!olMap || !lid) return;
    const layer = (olMap.getLayers && olMap.getLayers().getArray().find(l => l.get && l.get('lid') === lid));
    if (!layer) return;
    this.setCqlFilterOnLayer(layer, cql);
  },

  customLoader (tile, src) {
    const client = new XMLHttpRequest();
    client.responseType = 'blob';
    client.open('GET', src);
    client.setRequestHeader('Authorization', 'Basic ' + window.btoa('data_services' + ':' + 'data_services'));
    client.onload = function () {
      tile.getImage().src = URL.createObjectURL(client.response);
    };
    client.send();
  }

}
